import React, { useState, useEffect, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { isXlsxBuffer, decodeText } from './spreadsheetBytes';
import {
    SPREADSHEET_TYPES,
    CSV_MIME_TYPES,
    ARROW_UP,
    ARROW_DOWN,
    ARROW_LEFT,
    ARROW_RIGHT,
    ARROW_KEYS,
    MAX_PREVIEW_ROWS,
    MAX_PREVIEW_COLS,
} from './constants';
import { isEmpty, fmt } from '../../utils/utils';
import styles from './DocumentViewer.module.scss';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

type SheetData = { name: string; rows: string[][] };

/** Parse one worksheet, capping the used range to MAX_PREVIEW_ROWS × MAX_PREVIEW_COLS
 *  so sheet_to_json (with defval:'') can never materialize an unbounded array.
 *  Returns the bounded rows plus whether the real range exceeded the row/col cap. */
const sheetToBoundedRows = (worksheet: XLSX.WorkSheet): { rows: string[][]; truncatedRows: boolean; truncatedCols: boolean } => {
    const ref = worksheet['!ref'];
    if (!ref) return { rows: [], truncatedRows: false, truncatedCols: false };

    const range = XLSX.utils.decode_range(ref);
    const truncatedRows = range.e.r - range.s.r + 1 > MAX_PREVIEW_ROWS;
    const truncatedCols = range.e.c - range.s.c + 1 > MAX_PREVIEW_COLS;
    const boundedRange = {
        s: range.s,
        e: {
            r: Math.min(range.e.r, range.s.r + MAX_PREVIEW_ROWS - 1),
            c: Math.min(range.e.c, range.s.c + MAX_PREVIEW_COLS - 1),
        },
    };
    const rows = XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        raw: false,
        defval: '',
        range: XLSX.utils.encode_range(boundedRange),
    });
    return { rows, truncatedRows, truncatedCols };
};

const SpreadsheetViewer: React.FC<{ fileUrl: string; mimeType: string }> = ({ fileUrl, mimeType }) => {
    const [sheets, setSheets] = useState<SheetData[]>([]);
    const [activeSheet, setActiveSheet] = useState(0);
    const [truncatedRows, setTruncatedRows] = useState(false);
    const [truncatedCols, setTruncatedCols] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [focusedCell, setFocusedCell] = useState<[number, number]>([0, 0]);
    const [tableHasFocus, setTableHasFocus] = useState(false);
    const [selectionStart, setSelectionStart] = useState<[number, number]>([0, 0]);
    const [selectionEnd, setSelectionEnd] = useState<[number, number]>([0, 0]);
    const [hasSelection, setHasSelection] = useState(false);
    const tableRef = useRef<HTMLTableElement>(null);
    const isDragging = useRef(false);

    // Auto-focus first cell after data loads so arrow keys work immediately
    useEffect(() => {
        if (!loading && sheets.length > 0) {
            requestAnimationFrame(() => {
                tableRef.current?.querySelector<HTMLElement>('thead tr th')?.focus();
            });
        }
    }, [loading]);

    // Reset keyboard focus and selection when switching sheets
    useEffect(() => {
        setFocusedCell([0, 0]);
        setSelectionStart([0, 0]);
        setSelectionEnd([0, 0]);
        setHasSelection(false);
        requestAnimationFrame(() => {
            tableRef.current?.querySelector<HTMLElement>('thead tr th')?.focus();
        });
    }, [activeSheet]);

    // End drag selection when mouse is released anywhere
    useEffect(() => {
        const handleMouseUp = () => {
            isDragging.current = false;
        };
        document.addEventListener('mouseup', handleMouseUp);
        return () => document.removeEventListener('mouseup', handleMouseUp);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        const parse = async () => {
            try {
                const response = await fetch(fileUrl, { signal: controller.signal });
                if (!response.ok) throw new Error('Failed to fetch file');

                const buffer = await response.arrayBuffer();
                // Content-based detection: a file declared as CSV may actually be XLSX
                const isXlsx = isXlsxBuffer(buffer);
                const isCsv = CSV_MIME_TYPES.includes(mimeType);
                if (isXlsx || !isCsv) {
                    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
                    let anyRowsTruncated = false;
                    let anyColsTruncated = false;
                    const parsed: SheetData[] = workbook.SheetNames.map((name) => {
                        const bounded = sheetToBoundedRows(workbook.Sheets[name]);
                        if (bounded.truncatedRows) anyRowsTruncated = true;
                        if (bounded.truncatedCols) anyColsTruncated = true;
                        return { name, rows: bounded.rows };
                    });
                    setSheets(parsed.filter((s) => s.rows.length > 0));
                    setTruncatedRows(anyRowsTruncated);
                    setTruncatedCols(anyColsTruncated);
                } else {
                    const text = decodeText(buffer);
                    const result = Papa.parse<string[]>(text, { skipEmptyLines: true, preview: MAX_PREVIEW_ROWS + 1 });
                    const data = result.data;
                    const csvTruncatedRows = data.length > MAX_PREVIEW_ROWS;
                    const csvTruncatedCols = data.some((row) => row.length > MAX_PREVIEW_COLS);
                    const boundedRows = data.slice(0, MAX_PREVIEW_ROWS).map((row) => row.slice(0, MAX_PREVIEW_COLS));
                    setSheets([{ name: 'Sheet1', rows: boundedRows }]);
                    setTruncatedRows(csvTruncatedRows);
                    setTruncatedCols(csvTruncatedCols);
                }
                setLoading(false);
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
            }
        };
        parse();
        return () => controller.abort();
    }, [fileUrl, mimeType]);

    const aggregation = useMemo(() => {
        if (!hasSelection || sheets.length === 0) return null;
        const current = sheets[activeSheet];
        const minRow = Math.min(selectionStart[0], selectionEnd[0]);
        const maxRow = Math.max(selectionStart[0], selectionEnd[0]);
        const minCol = Math.min(selectionStart[1], selectionEnd[1]);
        const maxCol = Math.max(selectionStart[1], selectionEnd[1]);

        const allValues: string[] = [];
        for (let r = minRow; r <= maxRow; r++) {
            for (let c = minCol; c <= maxCol; c++) {
                const val = current.rows[r]?.[c];
                if (!isEmpty(val)) allValues.push(String(val));
            }
        }

        const numericValues = allValues.map((v) => parseFloat(v.replace(/[,\s]/g, ''))).filter((n) => !isNaN(n) && isFinite(n));
        const count = allValues.length;
        if (count === 0) return null;
        if (numericValues.length === 0) return { count };

        const sum = numericValues.reduce((a, b) => a + b, 0);
        const avg = sum / numericValues.length;
        const min = Math.min(...numericValues);
        const max = Math.max(...numericValues);
        const sorted = [...numericValues].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];

        return { count, sum, avg, min, max, median };
    }, [hasSelection, selectionStart, selectionEnd, sheets, activeSheet]);

    if (loading)
        return (
            <div className={styles.loadingBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
            </div>
        );

    if (error || sheets.length === 0)
        return (
            <div className={styles.errorBox}>
                <p className={styles.errorText}>{error ? 'Unable to preview this file' : 'This file has no data to preview'}</p>
                <button type="button" onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')} className={styles.errorButton}>
                    Open in New Tab
                </button>
            </div>
        );

    const current = sheets[activeSheet];

    const truncationMessage =
        truncatedRows && truncatedCols
            ? `Preview limited to the first ${MAX_PREVIEW_ROWS} rows × ${MAX_PREVIEW_COLS} columns.`
            : truncatedRows
            ? `Preview limited to the first ${MAX_PREVIEW_ROWS} rows.`
            : `Preview limited to the first ${MAX_PREVIEW_COLS} columns.`;

    const isInSelection = (row: number, col: number): boolean => {
        if (!hasSelection) return false;
        const minRow = Math.min(selectionStart[0], selectionEnd[0]);
        const maxRow = Math.max(selectionStart[0], selectionEnd[0]);
        const minCol = Math.min(selectionStart[1], selectionEnd[1]);
        const maxCol = Math.max(selectionStart[1], selectionEnd[1]);
        return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
    };

    const selMinRow = Math.min(selectionStart[0], selectionEnd[0]);
    const selMaxRow = Math.max(selectionStart[0], selectionEnd[0]);
    const selMinCol = Math.min(selectionStart[1], selectionEnd[1]);
    const selMaxCol = Math.max(selectionStart[1], selectionEnd[1]);

    const getSelectionShadow = (row: number, col: number): React.CSSProperties => {
        if (!hasSelection) return {};
        if (row < selMinRow || row > selMaxRow || col < selMinCol || col > selMaxCol) return {};
        // Anchor cell — bold inset border on all sides
        if (row === selectionStart[0] && col === selectionStart[1]) {
            return { boxShadow: 'inset 0 0 0 0.125rem #6021B3' };
        }
        // Rest of selection — thin inset shadow on outer edges only
        const color = '#9c70d4';
        const w = '0.0625rem';
        const shadows: string[] = [];
        if (row === selMinRow) shadows.push(`inset 0 ${w} 0 0 ${color}`);
        if (row === selMaxRow) shadows.push(`inset 0 -${w} 0 0 ${color}`);
        if (col === selMinCol) shadows.push(`inset ${w} 0 0 0 ${color}`);
        if (col === selMaxCol) shadows.push(`inset -${w} 0 0 0 ${color}`);
        return shadows.length > 0 ? { boxShadow: shadows.join(', ') } : {};
    };

    const focusCell = (row: number, col: number) => {
        setFocusedCell([row, col]);
        const sel = row === 0 ? `thead tr th:nth-child(${col + 1})` : `tbody tr:nth-child(${row}) td:nth-child(${col + 1})`;
        tableRef.current?.querySelector<HTMLElement>(sel)?.focus();
    };

    const handleCellMouseDown = (e: React.MouseEvent, row: number, col: number) => {
        if (e.shiftKey && hasSelection) {
            // Shift+click: extend existing selection, don't start a drag
            setSelectionEnd([row, col]);
        } else {
            // Normal click: start new selection and begin drag
            isDragging.current = true;
            setSelectionStart([row, col]);
            setSelectionEnd([row, col]);
            setHasSelection(true);
        }
        setFocusedCell([row, col]);
    };

    const handleCellMouseEnter = (row: number, col: number) => {
        if (isDragging.current) {
            setSelectionEnd([row, col]);
        }
    };

    const handleCellKeyDown = (e: React.KeyboardEvent, row: number, col: number) => {
        if (!(ARROW_KEYS as readonly string[]).includes(e.key)) return;
        e.preventDefault();

        if (e.shiftKey) {
            // Extend selection from selectionEnd (not the focused anchor cell)
            let newRow = selectionEnd[0];
            let newCol = selectionEnd[1];
            switch (e.key) {
                case ARROW_UP:
                    newRow--;
                    break;
                case ARROW_DOWN:
                    newRow++;
                    break;
                case ARROW_LEFT:
                    newCol--;
                    break;
                case ARROW_RIGHT:
                    newCol++;
                    break;
            }
            newRow = Math.max(0, Math.min(current.rows.length - 1, newRow));
            newCol = Math.max(0, Math.min((current.rows[newRow]?.length ?? 1) - 1, newCol));
            setSelectionEnd([newRow, newCol]);
            setHasSelection(true);
            // Keep DOM focus on anchor so next Shift+Arrow continues extending correctly
        } else {
            // Collapse selection and move anchor
            let newRow = row;
            let newCol = col;
            switch (e.key) {
                case ARROW_UP:
                    newRow--;
                    break;
                case ARROW_DOWN:
                    newRow++;
                    break;
                case ARROW_LEFT:
                    newCol--;
                    break;
                case ARROW_RIGHT:
                    newCol++;
                    break;
            }
            newRow = Math.max(0, Math.min(current.rows.length - 1, newRow));
            newCol = Math.max(0, Math.min((current.rows[newRow]?.length ?? 1) - 1, newCol));
            setSelectionStart([newRow, newCol]);
            setSelectionEnd([newRow, newCol]);
            setHasSelection(true);
            focusCell(newRow, newCol);
        }
    };

    return (
        <div className={styles.spreadsheetWrapper}>
            {sheets.length > 1 && (
                <div className={styles.sheetTabBar}>
                    {sheets.map((sheet, i) => (
                        <button
                            key={sheet.name}
                            type="button"
                            onClick={() => setActiveSheet(i)}
                            className={clsx(styles.sheetTab, i === activeSheet && styles.sheetTabActive)}
                        >
                            {sheet.name}
                        </button>
                    ))}
                </div>
            )}
            {(truncatedRows || truncatedCols) && (
                <div className={styles.truncationBanner}>
                    <span>{truncationMessage}</span>
                    <button
                        type="button"
                        onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                        className={styles.errorButton}
                    >
                        Open in New Tab
                    </button>
                </div>
            )}
            <div className={styles.tableScrollContainer}>
                <table
                    role="grid"
                    ref={tableRef}
                    className={styles.spreadsheetTable}
                    onFocus={() => setTableHasFocus(true)}
                    onBlur={(e) => {
                        if (!tableRef.current?.contains(e.relatedTarget as Node)) {
                            setTableHasFocus(false);
                        }
                    }}
                >
                    <thead>
                        <tr role="row">
                            {current.rows[0]?.map((cell, ci) => {
                                const isAnchor = hasSelection && selectionStart[0] === 0 && selectionStart[1] === ci;
                                const isHandle = hasSelection && selMaxRow === 0 && selMaxCol === ci;
                                const inSel = isInSelection(0, ci);
                                return (
                                    <th
                                        key={ci}
                                        role="columnheader"
                                        tabIndex={focusedCell[0] === 0 && focusedCell[1] === ci ? 0 : -1}
                                        aria-selected={inSel}
                                        onMouseDown={(e) => handleCellMouseDown(e, 0, ci)}
                                        onMouseEnter={() => handleCellMouseEnter(0, ci)}
                                        onKeyDown={(e) => handleCellKeyDown(e, 0, ci)}
                                        onFocus={() => setFocusedCell([0, ci])}
                                        className={clsx(
                                            styles.headerCell,
                                            inSel && !isAnchor && styles.headerCellSelected,
                                            isHandle && styles.cellHandle,
                                        )}
                                        style={getSelectionShadow(0, ci)}
                                    >
                                        {cell}
                                        {isHandle && <span className={styles.handleDot} />}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {current.rows.slice(1).map((row, ri) => (
                            <tr key={ri} role="row">
                                {row.map((cell, ci) => {
                                    const isAnchor = hasSelection && selectionStart[0] === ri + 1 && selectionStart[1] === ci;
                                    const isHandle = hasSelection && selMaxRow === ri + 1 && selMaxCol === ci;
                                    const inSel = isInSelection(ri + 1, ci);
                                    return (
                                        <td
                                            key={ci}
                                            role="gridcell"
                                            tabIndex={focusedCell[0] === ri + 1 && focusedCell[1] === ci ? 0 : -1}
                                            aria-selected={inSel}
                                            onMouseDown={(e) => handleCellMouseDown(e, ri + 1, ci)}
                                            onMouseEnter={() => handleCellMouseEnter(ri + 1, ci)}
                                            onKeyDown={(e) => handleCellKeyDown(e, ri + 1, ci)}
                                            onFocus={() => setFocusedCell([ri + 1, ci])}
                                            className={clsx(
                                                styles.dataCell,
                                                inSel && !isAnchor ? styles.dataCellSelected : ri % 2 !== 0 ? styles.dataCellOdd : null,
                                                isHandle && styles.cellHandle,
                                            )}
                                            style={getSelectionShadow(ri + 1, ci)}
                                        >
                                            {cell}
                                            {isHandle && <span className={styles.handleDot} />}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {aggregation && (
                <div role="status" aria-live="polite" aria-label="Selection aggregation" className={styles.aggregationBar}>
                    <span>Count: {aggregation.count}</span>
                    {'sum' in aggregation && (
                        <>
                            <span>Sum: {fmt(aggregation.sum!)}</span>
                            <span>Avg: {fmt(aggregation.avg!)}</span>
                            <span>Min: {fmt(aggregation.min!)}</span>
                            <span>Max: {fmt(aggregation.max!)}</span>
                            <span>Median: {fmt(aggregation.median!)}</span>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const PdfViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);
        setBlobUrl(null);

        const fetchPdf = async () => {
            try {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch PDF: ${response.status}`);
                }
                const blob = await response.blob();
                if (isMounted) {
                    const url = URL.createObjectURL(blob);
                    setBlobUrl(url);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error fetching PDF:', err);
                if (isMounted) {
                    setError(err instanceof Error ? err.message : 'Failed to load PDF');
                    setLoading(false);
                }
            }
        };

        fetchPdf();

        return () => {
            isMounted = false;
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [fileUrl]);

    if (loading) {
        return (
            <div className={styles.loadingBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
            </div>
        );
    }

    if (error || !blobUrl) {
        return (
            <div className={styles.errorBox}>
                <p className={styles.errorText}>Unable to preview this PDF</p>
                <div className={styles.errorButtonRow}>
                    <button
                        type="button"
                        onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                        className={styles.errorButton}
                    >
                        Open in New Tab
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pdfContainer}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                    fileUrl={blobUrl}
                    plugins={[defaultLayoutPluginInstance]}
                    renderError={() => (
                        <div className={styles.errorBoxFull}>
                            <p className={styles.errorText}>Unable to render this PDF</p>
                            <button onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')} className={styles.errorButton}>
                                Open in New Tab
                            </button>
                        </div>
                    )}
                />
            </Worker>
        </div>
    );
};

interface FileViewerProps {
    fileUrl: string;
    mimeType?: string;
}

/** Pure router — no hooks here, each branch renders its own component. */
const DocumentViewer: React.FC<FileViewerProps> = ({ fileUrl, mimeType }) => {
    if (mimeType?.startsWith('image/')) {
        return (
            <div className={styles.imageContainer}>
                <img src={fileUrl} alt="preview" className={styles.previewImage} />
            </div>
        );
    }

    if (mimeType && SPREADSHEET_TYPES.includes(mimeType)) {
        return <SpreadsheetViewer fileUrl={fileUrl} mimeType={mimeType} />;
    }

    return <PdfViewer fileUrl={fileUrl} />;
};

export default DocumentViewer;
